import './sepho-wave.css';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';

const SCRIPT_URL = '/build/games/sepho-wave/sepho-wave.min.js';

import imgGameIcon from '../../images/game-icon.svg';
import imgBackground from './images/bg-waves.jpg';
import imgPlayer from './images/boat.webp';
import imgIsland1 from './images/island-1.svg';
import imgIsland2 from './images/island-2.svg';
import imgIsland3 from './images/island-3.svg';
import imgLifeFull from './images/heart-full.svg';
import imgLifeEmpty from './images/heart-empty.svg';
import imgRipple from './images/ripple.svg';
import imgBonusScore from './images/bonus-points.svg';
import imgBonusTime from './images/bonus-time.svg';
import imgBonusSlow from './images/bonus-slow.svg';
import imgRotate from './images/rotate-phone.svg';

export const WELCOME_CONFIG = {
    icon: imgGameIcon,
    title: 'WITAJ W ISLAND ESCAPE',
    description: '<p>Przygotuj się na dynamiczną grę, w której liczy się refleks, kontrola i szybka reakcja! ' +
        'Twoim zadaniem jest sterowanie łódką, omijanie wysp i zbieranie bonusów pojawiających się na trasie.</p>' +
        '<p>Uważaj jednak – łódka płynie coraz szybciej. Masz 3 życia – każda kolizja z wyspą lub wypłynięcie poza ekran odbiera 1 życie. Utrata wszystkich 3 kończy grę. Im dłużej utrzymasz się na trasie, tym lepszy wynik!</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol>' +
            '<li>Przytrzymaj ekran, aby łódka płynęła wyżej, i puść, aby opadała.</li>' +
            '<li>Omijaj wyspy oraz krawędzie ekranu.</li>' +
            '<li>Zbieraj bonusy na trasie i postaraj się dopłynąć jak najdalej!</li>' +
            '</ol>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ Punkty naliczają się za przebyty dystans.</li>' +
            '<li>🎁 Bonusy na trasie mogą dodać punkty, czas lub chwilowo spowolnić łódkę.</li>' +
            '<li>❌ Kolizja z wyspą albo wypłynięcie poza ekran odbiera 1 życie (3 życia łącznie – utrata wszystkich kończy grę)</li>' +
            '</ul>' +
            '<p>Maksymalny wynik to 40 punktów.</p>' +
            '<p>Wypłyń na trasę i sprawdź, jak daleko uda Ci się dopłynąć! 🌊✨</p>',
    },
};

const DEFAULT_ASSETS_CONFIG = {
    texts: {
        gameTitle: 'Island Escape',
        gameSubtitle: 'Omijaj wyspy i zbieraj bonusy!',
        startButton: 'Start!',
        resultTitle: 'Koniec gry!',
        resultScore: 'Wynik',
        resultDistance: 'Dystans',
        resultTime: 'Czas gry',
        playAgain: 'Zagraj ponownie',
        loading: 'Ładowanie...',
        hudPoints: 'PUNKTY',
        hudTime: 'CZAS',
        rotatePrompt: 'Obróć urządzenie poziomo, aby grać',
    },
    images: {
        background: imgBackground,
        player: imgPlayer,
        island1: imgIsland1,
        island2: imgIsland2,
        island3: imgIsland3,
        lifeIndicator: imgLifeFull,
        lostLifeIndicator: imgLifeEmpty,
        ripple: imgRipple,
        rotateIcon: imgRotate,
        bonusLabels: {
            score: imgBonusScore,
            time: imgBonusTime,
            slowdown: imgBonusSlow,
        },
    },
    sounds: {
        collect: null,
        hit: null,
        gameOver: null,
    },
    colors: {
        skyTop: '#80FFF6',
        skyBottom: '#FF66CC',
        bgGradientFrom: '#40D6F2',
        bgGradientTo: '#00ADEE',
        player: '#1565C0',
        obstacle: '#388E3C',
        obstacleShore: '#8D6E63',
        bonusScore: '#F9A825',
        bonusTime: '#00897B',
        bonusSlowdown: '#7B1FA2',
        timerBarDanger: '#F44336',
    },
};

const DEFAULT_GAME_CONFIG = {
    timeLimitSeconds: 60,
    initialSpeed: 280,
    speedIncrement: 14,
    maxSpeed: 900,
    boatSpeed: 250,
    pixelsPerScorePoint: 1600,
    slowdownFactor: 0.7,
    slowdownDuration: 5,
    obstacleSpawnMinInterval: 1.5,
    obstacleSpawnMaxInterval: 3.5,
    bonusSpawnMinInterval: 3.0,
    bonusSpawnMaxInterval: 7.0,
    obstacleMinWidth: 120,
    obstacleMaxWidth: 350,
    obstacleGap: 200,
    obstaclePairChance: 0.20,
    lives: 3,
    invulnerabilityDuration: 3,
    bonusSize: 80,
    transitionDuration: 200,
};

let gameInstance  = null;
let currentTaskId = null;
let sessionToken  = null;
let eventSeq      = 0;
let _startTime = null;
let _pausedMs = 0;
let _pauseStart = null;
let _orientationMql = null;
let _onLandscapeFullscreen = null;

function resetSessionState() {
    currentTaskId = null;
    sessionToken  = null;
    eventSeq      = 0;
    _startTime = null;
    _pausedMs = 0;
    _pauseStart = null;
}

function getElapsedMs() {
    if (_startTime === null) return 0;
    const activePause = _pauseStart !== null ? Date.now() - _pauseStart : 0;
    return Math.max(0, Date.now() - _startTime - _pausedMs - activePause);
}

function sendEvent(path, taskId, body) {
    if (!sessionToken) return;
    fetch(`/api/task/${taskId}/sepho-wave/${path}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({sessionToken, ...body}),
    }).catch(() => { /* fire-and-forget */
    });
}

function attachEventListeners(taskId, modal) {
    if (!gameInstance?.eventBus) return;

    gameInstance.eventBus.on('game:start', () => {
        sessionToken = gameInstance.sessionToken ?? null;
        eventSeq = 0;
        _startTime = Date.now();
        _pausedMs = 0;
        _pauseStart = null;
    });

    // First interaction (first touch): lock the game server-side (NEW -> STARTED,
    // records the daily check-in). A 409 means the play was already consumed
    // elsewhere — stop the running game and show the blocked message.
    gameInstance.eventBus.on('game:lock', () => {
        if (!sessionToken) return;
        fetch(`/api/task/${taskId}/sepho-wave/begin`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({sessionToken}),
        })
            .then(async (res) => {
                if (res.status !== 409) return;
                const data = await res.json().catch(() => ({}));
                if (gameInstance) {
                    try { gameInstance.destroy(); } catch { /* ignore */ }
                    gameInstance = null;
                }
                renderGameMessage(modal, startBlockedMessage(data?.error));
            })
            .catch(() => { /* fire-and-forget; submit locks defensively as a fallback */ });
    });

    gameInstance.eventBus.on('life:lost', ({livesRemaining}) => {
        if (!sessionToken) return;
        eventSeq++;
        sendEvent('life-lost', taskId, {
            seq: eventSeq,
            elapsedMs: getElapsedMs(),
            livesRemaining: livesRemaining ?? 0,
        });
    });

    gameInstance.eventBus.on('bonus:collected', ({type, value}) => {
        if (!sessionToken) return;
        eventSeq++;
        sendEvent('bonus-collected', taskId, {
            seq: eventSeq,
            elapsedMs: getElapsedMs(),
            type: type ?? '',
            value: value ?? 0,
        });
    });

    gameInstance.eventBus.on('game:pause', () => {
        if (!sessionToken) return;
        _pauseStart = Date.now();
        eventSeq++;
        sendEvent('pause', taskId, {seq: eventSeq, elapsedMs: getElapsedMs()});
    });

    gameInstance.eventBus.on('game:resume', () => {
        if (!sessionToken) return;
        if (_pauseStart !== null) {
            _pausedMs += Date.now() - _pauseStart;
            _pauseStart = null;
        }
        eventSeq++;
        sendEvent('resume', taskId, {seq: eventSeq, elapsedMs: getElapsedMs()});
    });
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

function contentEl(modal) {
    return modal.querySelector('.game-modal__content');
}

// Mobile browser only — exclude desktop (fine-pointer) and installed PWAs,
// where element fullscreen is either unnecessary or unsupported.
function isMobileBrowser() {
    const isMobile = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || window.navigator.standalone === true;
    return isMobile && !isStandalone;
}

function requestGameFullscreen(el) {
    if (!el || document.fullscreenElement) return;
    const req = el.requestFullscreen ?? el.webkitRequestFullscreen;
    if (typeof req !== 'function') return;
    try {
        const p = req.call(el);
        // Browsers reject when not granted (no user activation, iOS, etc.) — ignore.
        if (p && typeof p.catch === 'function') p.catch(() => { /* not allowed */ });
    } catch { /* ignore */ }
}

function exitGameFullscreen() {
    if (!document.fullscreenElement) return;
    try {
        const p = (document.exitFullscreen ?? document.webkitExitFullscreen)?.call(document);
        if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
    } catch { /* ignore */ }
}

// After the player rotates to landscape, try to enter fullscreen so the game
// uses the whole viewport. Only on mobile browsers (not PWA). The rotation gives
// the browser the user-activation context most engines require for fullscreen.
// Rotating back to portrait exits fullscreen again.
function attachLandscapeFullscreen(container) {
    if (!isMobileBrowser()) return;
    _onLandscapeFullscreen = () => {
        if (window.matchMedia('(orientation: landscape)').matches) {
            requestGameFullscreen(container);
        } else {
            exitGameFullscreen();
        }
    };
    _orientationMql = window.matchMedia('(orientation: landscape)');
    _orientationMql.addEventListener?.('change', _onLandscapeFullscreen);
    window.addEventListener('orientationchange', _onLandscapeFullscreen);
    // Handle the case where the device is already in landscape on open.
    _onLandscapeFullscreen();
}

function detachLandscapeFullscreen() {
    if (_onLandscapeFullscreen) {
        _orientationMql?.removeEventListener?.('change', _onLandscapeFullscreen);
        window.removeEventListener('orientationchange', _onLandscapeFullscreen);
        _onLandscapeFullscreen = null;
        _orientationMql = null;
    }
    exitGameFullscreen();
}

export function renderSephoWave(modal, data, markCardDone) {
    contentEl(modal).innerHTML = '';
    resetSessionState();
    startGame(modal, data, markCardDone);
}

function startGame(modal, data, markCardDone) {
    const taskId = data.task.id;
    currentTaskId = taskId;
    const cfg = data.sephoWaveGame.config;

    const el = contentEl(modal);
    const containerId = 'sw-game-container';
    const container = document.createElement('div');
    container.id = containerId;
    container.className = 'sw-game';
    el.appendChild(container);

    loadScript(SCRIPT_URL)
        .then(() => {
            if (typeof SephoWaveGame === 'undefined') {
                console.error('SephoWaveGame: script loaded but SephoWaveGame is not defined');
                return;
            }

            gameInstance = SephoWaveGame.init(containerId, {
                assetsConfig: DEFAULT_ASSETS_CONFIG,
                gameConfig: {
                    ...DEFAULT_GAME_CONFIG,
                    timeLimitSeconds: cfg.timeLimitSeconds ?? DEFAULT_GAME_CONFIG.timeLimitSeconds,
                    bonusTypes: cfg.bonusTypes ?? [],
                    autoStart: true,
                },
                apiConfig: {
                    useMock: false,
                    endpoints: {
                        startGame: `/api/task/${taskId}/sepho-wave/start`,
                        submitResult: `/api/task/${taskId}/sepho-wave/submit`,
                        heartbeat: `/api/task/${taskId}/sepho-wave/heartbeat`,
                    },
                },
                callbacks: {
                    onError: (error) => {
                        if (error?.status === 409 && (error.data?.error === 'already_completed' || error.data?.error === 'game_in_progress')) {
                            renderGameMessage(modal, startBlockedMessage(error.data?.error));
                            return;
                        }
                        document.dispatchEvent(new CustomEvent('game-modal:request-close'));
                        alert(`Błąd gry: ${error}`);
                    },
                    onComplete: (result) => {
                        if (gameInstance) {
                            try {
                                gameInstance.destroy();
                            } catch { /* ignore */
                            }
                            gameInstance = null;
                        }
                        markCardDone({rankingPointsEarned: result.rankingPointsEarned ?? 0, redeemablePointsEarned: result.redeemablePointsEarned ?? 0, checkIn: result.checkIn});
                        document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                            detail: {
                                rankingPoints: result.rankingPointsEarned ?? 0,
                                redeemablePoints: result.redeemablePointsEarned ?? 0,
                            },
                        }));
                    },
                },
            });
            attachEventListeners(taskId, modal);
            attachLandscapeFullscreen(container);
        })
        .catch((e) => console.error('SephoWaveGame: failed to load game script', e));
}

export function pauseSephoWave() {
    if (gameInstance) {
        try {
            gameInstance.pause();
        } catch { /* ignore */
        }
    }
}

export function resumeSephoWave() {
    if (gameInstance) {
        try {
            gameInstance.resume();
        } catch { /* ignore */
        }
    }
}

export async function forceEndSephoWave() {
    if (gameInstance) {
        try {
            if (currentTaskId !== null) {
                await fetch(`/api/task/${currentTaskId}/sepho-wave/flag-force-end`, { method: 'POST' });
            }
            gameInstance.resume();
            gameInstance.forceEnd();
        } catch { /* ignore */
        }
    }
}

export function teardownSephoWave(modal) {
    detachLandscapeFullscreen();
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
    resetSessionState();
    const el = contentEl(modal);
    if (el) el.innerHTML = '';
}
